import { Injectable, inject } from '@angular/core';
import { Client } from '../../models/client';
import { Meal } from '../../models/meal';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, combineLatest } from 'rxjs';
import {
  catchError,
  publishReplay,
  refCount,
  map,
  take,
  tap,
  filter,
} from 'rxjs/operators';
import { ClientFoundResponse } from '../../models/clientFoundResponse';
import { environment } from '../../../../environments/environment';
import { Title } from '@angular/platform-browser';
import { UIService } from '../../+state/ui/ui.service';
import { MenuType } from '../../models/menuType.enum';
import { Router } from '@angular/router';
import { ClientIdTextAccountModel } from 'app/+shared/models/client-id-text-account.model';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private titleService = inject(Title);
  private uiService = inject(UIService);

  private allClientsSource = new BehaviorSubject<ClientIdTextAccountModel[]>(
    []
  );
  allClients$ = this.allClientsSource.asObservable();

  private clientSource = new BehaviorSubject<Client | null>(null);
  currentClient$ = this.clientSource.asObservable();
  currentClientLocations$ = this.currentClient$.pipe(
    filter((client) => !!client),
    map((client) => (client.locations === undefined ? [] : client.locations))
  );

  currentSelectedLocation$ = combineLatest([
    this.currentClient$,
    this.uiService.currentLocationId$,
  ]).pipe(
    filter(([client, locationId]) => !!client && !!locationId),
    map(([client, locationId]) =>
      client.locations.find((location) => location.id === locationId)
    )
  );

  currentMeal$ = combineLatest([
    this.currentSelectedLocation$,
    this.uiService.currentMealId$,
  ]).pipe(
    map(([location, mealId]) => {
      if (!location || !mealId) {
        return null;
      }
      return location.meals.find((meal) => meal.id === mealId);
    })
  );

  private currentUrlSource = new BehaviorSubject<string>('');
  currentUrl$ = this.currentUrlSource.asObservable();

  anyClient(clientName: string): Observable<ClientFoundResponse> {
    const headers = new HttpHeaders();
    const params = new HttpParams().append('clientName', clientName);
    return this.http
      .get<ClientFoundResponse>(`${environment.apiUrl}/client/any`, {
        headers,
        params,
      })
      .pipe(
        take(1),
        publishReplay(1),
        refCount(),
        catchError(this.handleError)
      );
  }

  setClient(client: Client) {
    if (client) {
      this.clientSource.next(client);
    }
  }

  getById(clientId: number): Observable<Client> {
    const headers = new HttpHeaders();
    return this.http
      .get<Client>(`${environment.apiUrl}/client/${clientId}`, {
        headers,
      })
      .pipe(tap((client) => this.setClient(client)));
  }

  getClient(clientName: string): Observable<Client> {
    const headers = new HttpHeaders();
    const params = new HttpParams().append('clientName', clientName);
    return this.http
      .get<Client>(`${environment.apiUrl}/client`, { headers, params })
      .pipe(
        map((client) => {
          return client;
        }),
        publishReplay(1),
        refCount(),
        catchError(this.handleError)
      );
  }

  getAndSetClient(
    clientName: string,
    locationId: number,
    mealId: number,
    date: Date
  ): void {
    this.getClient(clientName)
      .pipe(
        take(1),
        tap((client) => {
          this.setClient(client);
          this.titleService.setTitle(`${client.name} - AVI Dish`);
          if (date) {
            this.uiService.setCurrentSelectedDate(date);
          }
          if (locationId) {
            const location = client.locations.find(
              (loc) => loc.id === locationId
            );
            this.uiService.setCurrentLocationId(location.id);
            if (mealId) {
              this.uiService.setCurrentMealId(mealId);
            }
          }
        })
      )
      .subscribe();
  }

  getMealsByLocationId = (selectedLocationId: number): Observable<Meal[]> =>
    this.currentClient$.pipe(
      map(
        (client) =>
          client.locations.find((loc) => loc.id === selectedLocationId).meals
      )
    );

  setCurrentUrl(url: string) {
    if (url) {
      this.currentUrlSource.next(url);
    }
  }

  private handleError(err: HttpErrorResponse) {
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }

  navigateToExpectedParameterizedRoute() {
    combineLatest([
      this.currentClient$,
      this.uiService.currentLocationId$,
      this.uiService.currentMealId$,
      this.uiService.currentSelectedDate$,
      this.uiService.currentMenuType$,
    ])
      .pipe(
        take(1),
        tap(([currentClient, locationId, mealId, date, menuType]) => {
          if (!locationId) {
            return;
          }

          const routeArray = [currentClient.urlExtension, locationId];

          if (mealId) {
            routeArray.push(mealId);
          }

          if (date) {
            routeArray.push(
              `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`
            );
          }

          if (menuType !== null) {
            routeArray.push(menuType === MenuType.Day ? 'day' : 'week');
          }

          this.router.navigate(routeArray);
        })
      )
      .subscribe();
  }

  getAllClients(
    userId = '',
    term = ''
  ): Observable<ClientIdTextAccountModel[]> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');
    const params = new HttpParams();
    if (userId) params.append('userId', userId);
    if (term) params.append('term', term);
    const endpoint = 'clients';

    return this.http
      .get<ClientIdTextAccountModel[]>(`${environment.apiUrl}/${endpoint}`, {
        headers,
        params,
      })
      .pipe(tap((clients) => this.allClientsSource.next(clients)));
  }
}
